import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Eye, Loader2, Plus, Trash2 } from "lucide-react";
import { formatCurrency as formatCurrencyBase } from "../lib/formatters";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { initializeLineItems, initializeFromBidSheets, filterUnapprovedManualCOs, round2 } from "../hooks/useBillingInitialization";
import { useConfirm } from "../components/ui/confirm-dialog";
import { UncodedItemsWarning } from "../components/job/billing/UncodedItemsWarning";
const formatCurrency = (amount) => formatCurrencyBase(amount, true);

const BillingApplicationPage = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const billingId = searchParams.get("billingId");
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const confirm = useConfirm();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [inputMode, setInputMode] = useState("dollar"); // "dollar" or "percent"

  // Form state
  const [billingNumber, setBillingNumber] = useState("");
  const [applicationDate, setApplicationDate] = useState(new Date().toISOString().split("T")[0]);
  const [periodTo, setPeriodTo] = useState("");
  const [retainagePercent, setRetainagePercent] = useState("0");
  const [lineItems, setLineItems] = useState([]);
  const [isFinalBilling, setIsFinalBilling] = useState(false);
  const [undoHistory, setUndoHistory] = useState([]);

  // Wrap setLineItems to track undo history
  const updateLineItems = (newItems) => {
    setUndoHistory(prev => [...prev, lineItems]);
    setLineItems(newItems);
  };

  const handleUndo = () => {
    if (undoHistory.length === 0) return;
    const previous = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setLineItems(previous);
    toast.success("Undo successful");
  };

  // Load job data
  useEffect(() => {
    fetchJobData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchJobData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/jobs/${jobId}`);
      const jobData = response.data;
      if (!jobData) {
        toast.error("Job not found");
        navigate(`/jobs`);
        return;
      }
      // Billing-category lookup (master materials + misc items) used when building the first SOV
      try {
        const [matRes, optRes] = await Promise.all([apiClient.get(`/materials`), apiClient.get(`/settings/options`)]);
        jobData._billingLookup = {
          materials: Object.fromEntries((matRes.data || []).filter((m) => m.billing_category).map((m) => [m.id, m.billing_category])),
          misc: Object.fromEntries((optRes.data?.misc_items || []).filter((m) => m.billing_category).map((m) => [m.id, m.billing_category])),
          miscByDescription: Object.fromEntries((optRes.data?.misc_items || []).filter((m) => m.billing_category && m.description).map((m) => [m.description.trim().toLowerCase(), m.billing_category])),
        };
      } catch {
        jobData._billingLookup = { materials: {}, misc: {} };
      }
      setJob(jobData);

      // If editing existing billing, load its data
      if (billingId) {
        const existingBilling = jobData.billings?.find((b) => b.id === billingId);
        if (existingBilling) {
          setBillingNumber(existingBilling.billing_number || "");
          setApplicationDate(existingBilling.application_date || new Date().toISOString().split("T")[0]);
          setPeriodTo(existingBilling.period_to || "");
          setRetainagePercent((existingBilling.retainage_percent ?? 0).toString());
          setIsFinalBilling(existingBilling.is_final_billing || false);
          if (existingBilling.line_items?.length > 0) {
            setLineItems(filterUnapprovedManualCOs(existingBilling.line_items, jobData));
          } else {
            try {
              setLineItems(initializeLineItems(jobData, existingBilling));
            } catch (error) {
              console.error("Error initializing line items:", error);
              toast.error("Error initializing billing data. Please try again.");
              setLineItems([]);
            }
          }
        }
      } else {
        // New billing - auto-increment number and inherit retainage from first billing
        const existingBillings = jobData.billings || [];
        const activeBillings = existingBillings.filter(b => b.status !== "void");
        const nextNumber = existingBillings.length + 1;
        setBillingNumber(nextNumber.toString());
        if (activeBillings.length > 0) {
          setRetainagePercent((activeBillings[0].retainage_percent ?? 0).toString());
        }

        // Auto-fill application date from job's billing day
        const billingDay = parseInt(jobData.billing_date);
        if (billingDay) {
          const now = new Date();
          const year = now.getFullYear();
          const month = now.getMonth();
          const lastDay = new Date(year, month + 1, 0).getDate();
          const day = Math.min(billingDay, lastDay);
          const appDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          setApplicationDate(appDate);
        }

        // Auto-fill period to = last day of current month
        const now = new Date();
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const periodToDate = `${lastDayOfMonth.getFullYear()}-${String(lastDayOfMonth.getMonth() + 1).padStart(2, "0")}-${String(lastDayOfMonth.getDate()).padStart(2, "0")}`;
        setPeriodTo(periodToDate);
        try {
          setLineItems(initializeLineItems(jobData, null));
        } catch (error) {
          console.error("Error initializing line items:", error);
          toast.error("Error initializing billing data. Please try again.");
          setLineItems([]);
        }
      }
    } catch (error) {
      console.error("Error fetching job:", error);
      toast.error("Failed to load job data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals - ALL values rounded to 2 decimal places
  const calculations = useMemo(() => {
    const originalContract = round2(job?.contract_amount || 0);
    const changeOrders = round2((job?.change_orders || [])
      .filter((co) => co.date_approved)
      .reduce((sum, co) => {
        return sum + (parseFloat(co.amount) || 0);
      }, 0));
    const contractSumToDate = round2(originalContract + changeOrders);

    const totalScheduledValue = round2(lineItems.reduce((sum, item) => sum + (item.scheduled_value || 0), 0));
    const totalFromPrevious = round2(lineItems.reduce((sum, item) => sum + (item.from_previous || 0), 0));
    const totalThisPeriod = round2(lineItems.reduce((sum, item) => sum + (item.this_period_amount || 0), 0));
    const totalMaterialStored = round2(lineItems.reduce((sum, item) => sum + (item.material_stored || 0), 0));
    const totalCompletedStored = round2(totalFromPrevious + totalThisPeriod + totalMaterialStored);

    const retainagePct = parseFloat(retainagePercent) || 0;
    const totalRetainage = isFinalBilling ? 0 : round2(totalCompletedStored * (retainagePct / 100));
    const retainageReleased = isFinalBilling ? round2(totalCompletedStored * (retainagePct / 100)) : 0;
    const totalEarnedLessRetainage = round2(totalCompletedStored - totalRetainage);

    // Previous certificates = sum of all previous ACTIVE (non-void) billings' current_payment_due
    const previousBillings = (job?.billings || []).filter(
      (b) => (!billingId || b.id !== billingId) && b.status !== "void"
    );
    const previousCertificates = round2(previousBillings.reduce(
      (sum, b) => sum + (b.current_payment_due || b.amount || 0),
      0
    ));

    const currentPaymentDue = round2(totalEarnedLessRetainage - previousCertificates);
    // Balance to finish includes retainage: E27 - E37
    const balanceToFinish = round2(contractSumToDate - totalEarnedLessRetainage);

    const percentComplete = contractSumToDate > 0 ? round2((totalCompletedStored / contractSumToDate) * 100) : 0;

    return {
      originalContract,
      changeOrders,
      contractSumToDate,
      totalScheduledValue,
      totalFromPrevious,
      totalThisPeriod,
      totalMaterialStored,
      totalCompletedStored,
      totalRetainage,
      retainageReleased,
      totalEarnedLessRetainage,
      previousCertificates,
      currentPaymentDue,
      balanceToFinish,
      percentComplete,
    };
  }, [job, lineItems, retainagePercent, billingId, isFinalBilling]);

  // Update line item - all monetary values rounded to 2 decimal places
  const updateLineItem = useCallback((index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index] };

      if (field === "description") {
        item.description = value;
      } else if (field === "scheduled_value") {
        item.scheduled_value = round2(parseFloat(value) || 0);
      } else if (field === "this_period_amount") {
        item.this_period_amount = round2(parseFloat(value) || 0);
        // Calculate percent (handle both positive and negative scheduled values)
        if (item.scheduled_value !== 0) {
          item.this_period_percent = round2((item.this_period_amount / item.scheduled_value) * 100);
        }
      } else if (field === "this_period_percent") {
        item.this_period_percent = round2(parseFloat(value) || 0);
        // Calculate amount - round to 2 decimal places
        item.this_period_amount = round2((item.this_period_percent / 100) * item.scheduled_value);
      } else if (field === "material_stored") {
        item.material_stored = round2(parseFloat(value) || 0);
      }

      // Recalculate totals - all rounded to 2 decimal places
      item.total_completed = round2((item.from_previous || 0) + item.this_period_amount + item.material_stored);
      item.percent_complete = item.scheduled_value !== 0 ? round2((item.total_completed / item.scheduled_value) * 100) : 0;
      item.balance_to_finish = round2(item.scheduled_value - item.total_completed);

      updated[index] = item;
      return updated;
    });
  }, []);

  // Delete line item
  const deleteLineItem = useCallback((index) => {
    setUndoHistory(prev => [...prev, lineItems]);
    setLineItems((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((item, i) => ({ ...item, item_number: i + 1 }));
    });
  }, [lineItems]);

  // Add new line item
  const addLineItem = useCallback(() => {
    setUndoHistory(prev => [...prev, lineItems]);
    setLineItems((prev) => {
      const newItem = {
        id: `custom-${Date.now()}`,
        item_number: prev.length + 1,
        description: "New Line Item",
        scheduled_value: 0,
        from_previous: 0,
        this_period_amount: 0,
        this_period_percent: 0,
        material_stored: 0,
        source_type: "custom",
        source_category: null,
        source_sheet_id: null,
        source_sheet_name: null,
      };
      return [...prev, newItem];
    });
  }, []);

  // Save billing - ensure all monetary values are rounded before saving
  const handleSave = async () => {
    if (!billingNumber.trim()) {
      toast.error("Please enter an application number");
      return;
    }

    setSaving(true);
    try {
      // Round all line item values before saving
      const roundedLineItems = lineItems.map(item => ({
        ...item,
        scheduled_value: round2(item.scheduled_value),
        from_previous: round2(item.from_previous),
        this_period_amount: round2(item.this_period_amount),
        this_period_percent: round2(item.this_period_percent),
        material_stored: round2(item.material_stored),
        total_completed: round2(item.total_completed),
        balance_to_finish: round2(item.balance_to_finish),
      }));

      const billingData = {
        billing_number: billingNumber,
        amount: round2(calculations.currentPaymentDue),
        application_date: applicationDate,
        period_to: periodTo,
        date_sent: applicationDate,
        retainage_percent: parseFloat(retainagePercent) || 0,
        is_final_billing: isFinalBilling,
        line_items: roundedLineItems,
        original_contract_sum: round2(calculations.originalContract),
        net_change_orders: round2(calculations.changeOrders),
        contract_sum_to_date: round2(calculations.contractSumToDate),
        total_completed_stored: round2(calculations.totalCompletedStored),
        total_retainage: round2(calculations.totalRetainage),
        retainage_released: round2(calculations.retainageReleased),
        total_earned_less_retainage: round2(calculations.totalEarnedLessRetainage),
        previous_certificates: round2(calculations.previousCertificates),
        current_payment_due: round2(calculations.currentPaymentDue),
        balance_to_finish: round2(calculations.balanceToFinish),
      };

      if (billingId) {
        await apiClient.put(`/jobs/${jobId}/billings/${billingId}`, billingData);
        toast.success("Billing updated successfully");
      } else {
        await apiClient.post(`/jobs/${jobId}/billings`, billingData);
        toast.success("Billing created successfully");
      }

      navigate(`/jobs/${jobId}?tab=billings`);
    } catch (error) {
      console.error("Error saving billing:", error);
      toast.error("Failed to save billing");
    } finally {
      setSaving(false);
    }
  };

  // SOV is editable if:
  // 1. Creating new billing (no billingId) - always editable since it will be the newest
  // 2. Editing the most recent ACTIVE billing (no active billings after this one)
  const canEditSOV = useMemo(() => {
    if (!billingId) {
      // New billing - always editable since it will be the newest
      return true;
    }
    // Editing existing billing - check if it's the most recent ACTIVE one
    const billings = job?.billings || [];
    const activeBillings = billings.filter((b) => b.status !== "void");
    const sortedBillings = [...activeBillings].sort(
      (a, b) => {
        const bNum = parseInt(String(b.billing_number || "0").split("-")[0] || "0") || 0;
        const aNum = parseInt(String(a.billing_number || "0").split("-")[0] || "0") || 0;
        return bNum - aNum;
      }
    );
    const mostRecentBilling = sortedBillings[0];
    return mostRecentBilling?.id === billingId;
  }, [billingId, job?.billings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/jobs/${jobId}?tab=billings`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Job
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Application for Payment</h1>
            <p className="text-muted-foreground">{job?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* General Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            General Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Application #</Label>
              <Input
                value={billingNumber}
                onChange={(e) => setBillingNumber(e.target.value)}
                placeholder="1"
                data-testid="billing-number-input"
              />
            </div>
            <div>
              <Label>Application Date</Label>
              <Input
                type="date"
                value={applicationDate}
                onChange={(e) => setApplicationDate(e.target.value)}
                data-testid="application-date-input"
              />
            </div>
            <div>
              <Label>Period To</Label>
              <Input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                data-testid="period-to-input"
              />
            </div>
            <div>
              <Label>Retainage %</Label>
              <NumericInput
                decimals={2}
                min={0}
                max={100}
                value={retainagePercent}
                onChange={(e) => setRetainagePercent(e.target.value)}
                data-testid="retainage-percent-input"
                disabled={isFinalBilling}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input
                type="checkbox"
                id="final-billing"
                checked={isFinalBilling}
                onChange={(e) => setIsFinalBilling(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="final-billing-checkbox"
              />
              <Label htmlFor="final-billing" className="text-red-600 font-semibold cursor-pointer">
                Final Billing (Release Retainage)
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Heads-up: bid items with no billing category (first SOV only) */}
      {!billingId && (job?.billings || []).filter((b) => b.status !== "void").length === 0 && (
        <UncodedItemsWarning job={job} jobId={jobId} />
      )}

      {/* Schedule of Values */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Schedule of Values (G703)</CardTitle>
            {canEditSOV && (
              <p className="text-sm text-muted-foreground mt-1">
                Customize your schedule of values below. This structure will carry forward to future billings.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="regenerate-sov-btn"
              onClick={async () => {
                const ok = await confirm({ title: "Regenerate from Bids", message: "WARNING: This will reset ALL current billing values and regenerate the schedule from your accepted bid sheets.\n\nAny manual edits to line items, amounts billed this period, and stored materials will be lost.\n\nAre you sure you want to continue?", confirmText: "Regenerate", confirmVariant: "destructive" });
                if (ok) {
                  const newItems = initializeFromBidSheets(job);
                  const total = newItems.reduce((s, i) => s + (i.scheduled_value || 0), 0);
                  updateLineItems(newItems);
                  toast.success(`Regenerated: ${newItems.length} items, total: $${total.toFixed(2)}`);
                }
              }}
            >
              Regenerate from Bids
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={undoHistory.length === 0}
              onClick={handleUndo}
              data-testid="undo-btn"
            >
              Undo
            </Button>
            <span className="text-sm text-muted-foreground">Entry Mode:</span>
            <Button
              variant={inputMode === "dollar" ? "default" : "outline"}
              size="sm"
              onClick={() => setInputMode("dollar")}
            >
              $ Amount
            </Button>
            <Button
              variant={inputMode === "percent" ? "default" : "outline"}
              size="sm"
              onClick={() => setInputMode("percent")}
            >
              % Complete
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="min-w-[140px]">Description</TableHead>
                  <TableHead className="text-right w-28">Scheduled Value</TableHead>
                  <TableHead className="text-right w-28">From Previous</TableHead>
                  <TableHead className="text-right w-32">
                    {inputMode === "dollar" ? "$ This Period" : "% This Period"}
                  </TableHead>
                  <TableHead className="text-right w-28">Material Stored</TableHead>
                  <TableHead className="text-right w-28">Total Completed</TableHead>
                  <TableHead className="text-right w-20">%</TableHead>
                  <TableHead className="text-right w-28">Balance</TableHead>
                  <TableHead className="text-right w-28">Retainage</TableHead>
                  {canEditSOV && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item, index) => {
                  if (!item) return null;
                  const totalCompleted = round2((item.from_previous || 0) + (item.this_period_amount || 0) + (item.material_stored || 0));
                  const scheduledVal = item.scheduled_value || 0;
                  const percentComplete = scheduledVal !== 0 ? round2((totalCompleted / scheduledVal) * 100) : 0;
                  const balance = round2(scheduledVal - totalCompleted);
                  const retainagePctNum = parseFloat(retainagePercent) || 0;
                  const itemRetainage = round2(totalCompleted * (retainagePctNum / 100));

                  return (
                    <TableRow key={item.id || index} className={index % 2 === 0 ? "bg-muted/20" : ""}>
                      <TableCell className="font-medium">{item.item_number || index + 1}</TableCell>
                      <TableCell>
                        {canEditSOV ? (
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, "description", e.target.value)}
                            className="h-8"
                            data-testid={`description-input-${index}`}
                          />
                        ) : (
                          <span className="font-medium">{item.description}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditSOV ? (
                          <NumericInput
                            decimals={2}
                            allowNegative={true}
                            className="w-28 text-right font-mono h-8"
                            value={item.scheduled_value ? Math.round(item.scheduled_value * 100) / 100 : ""}
                            onChange={(e) => updateLineItem(index, "scheduled_value", e.target.value)}
                            data-testid={`scheduled-value-input-${index}`}
                          />
                        ) : (
                          <span className="font-mono">{formatCurrency(item.scheduled_value)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(item.from_previous)}</TableCell>
                      <TableCell className="text-right">
                        <NumericInput
                          decimals={2}
                          allowNegative={true}
                          className="w-28 text-right font-mono h-8"
                          value={inputMode === "dollar" 
                            ? (item.this_period_amount ? Math.round(item.this_period_amount * 100) / 100 : "") 
                            : (item.this_period_percent ? Math.round(item.this_period_percent * 100) / 100 : "")}
                          onChange={(e) =>
                            updateLineItem(
                              index,
                              inputMode === "dollar" ? "this_period_amount" : "this_period_percent",
                              e.target.value
                            )
                          }
                          placeholder={inputMode === "dollar" ? "$0.00" : "0%"}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <NumericInput
                          decimals={2}
                          min={0}
                          className="w-28 text-right font-mono h-8"
                          value={item.material_stored ? Math.round(item.material_stored * 100) / 100 : ""}
                          onChange={(e) => updateLineItem(index, "material_stored", e.target.value)}
                          placeholder="$0.00"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totalCompleted)}</TableCell>
                      <TableCell className="text-right font-mono">{(isFinite(percentComplete) ? percentComplete : 0).toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(balance)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(itemRetainage)}</TableCell>
                      {canEditSOV && (
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Line Item?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove &quot;{item.description}&quot; from the schedule of values.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteLineItem(index)}
                                  className="bg-destructive text-destructive-foreground"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {/* Totals Row */}
                <TableRow className="bg-muted font-bold border-t-2">
                  <TableCell colSpan={2}>TOTALS</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalScheduledValue)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalFromPrevious)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalThisPeriod)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalMaterialStored)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalCompletedStored)}</TableCell>
                  <TableCell className="text-right font-mono">{(isFinite(calculations.percentComplete) ? calculations.percentComplete : 0).toFixed(1)}%</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.balanceToFinish)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(calculations.totalRetainage)}</TableCell>
                  {canEditSOV && <TableCell></TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Add Line Item Button */}
          <div className="mt-4">
            <Button variant="outline" onClick={addLineItem} data-testid="add-line-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Line Item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* G702 Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">G702 Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-xl space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span>1. ORIGINAL CONTRACT SUM</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.originalContract)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>2. Net change by Change Orders</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.changeOrders)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>3. CONTRACT SUM TO DATE (Line 1 + 2)</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.contractSumToDate)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>4. TOTAL COMPLETED & STORED TO DATE</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.totalCompletedStored)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>5. RETAINAGE ({isFinalBilling ? "RELEASED" : `${retainagePercent}% of Line 4`})</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.totalRetainage)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>6. TOTAL EARNED LESS RETAINAGE (Line 4 - 5)</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.totalEarnedLessRetainage)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>7. LESS PREVIOUS CERTIFICATES FOR PAYMENT</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.previousCertificates)}</span>
            </div>
            <div className="flex justify-between py-3 bg-primary/10 rounded px-3 -mx-3">
              <span className="font-bold">8. CURRENT PAYMENT DUE (Line 6 - 7)</span>
              <span className="font-mono font-bold text-lg">{formatCurrency(calculations.currentPaymentDue)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span>9. BALANCE TO FINISH, INCLUDING RETAINAGE</span>
              <span className="font-mono font-medium">{formatCurrency(calculations.balanceToFinish)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>G702/G703 Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 p-4 bg-white rounded border">
            {/* G702 Preview */}
            <div className="text-center border-b pb-4">
              <h2 className="text-xl font-bold">AIA DOCUMENT G702</h2>
              <p className="text-sm text-muted-foreground">APPLICATION AND CERTIFICATE FOR PAYMENT</p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>TO OWNER:</strong> {job?.client_name}</p>
                <p><strong>PROJECT:</strong> {job?.name}</p>
              </div>
              <div className="text-right">
                <p><strong>APPLICATION NO:</strong> {billingNumber}</p>
                <p><strong>APPLICATION DATE:</strong> {applicationDate}</p>
                <p><strong>PERIOD TO:</strong> {periodTo}</p>
              </div>
            </div>

            <div className="border rounded p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>1. ORIGINAL CONTRACT SUM</span><span>{formatCurrency(calculations.originalContract)}</span></div>
              <div className="flex justify-between"><span>2. Net change by Change Orders</span><span>{formatCurrency(calculations.changeOrders)}</span></div>
              <div className="flex justify-between border-t pt-2"><span>3. CONTRACT SUM TO DATE</span><span>{formatCurrency(calculations.contractSumToDate)}</span></div>
              <div className="flex justify-between"><span>4. TOTAL COMPLETED & STORED TO DATE</span><span>{formatCurrency(calculations.totalCompletedStored)}</span></div>
              <div className="flex justify-between"><span>5. RETAINAGE ({isFinalBilling ? "RELEASED" : `${retainagePercent}%`})</span><span>{formatCurrency(calculations.totalRetainage)}</span></div>
              <div className="flex justify-between"><span>6. TOTAL EARNED LESS RETAINAGE</span><span>{formatCurrency(calculations.totalEarnedLessRetainage)}</span></div>
              <div className="flex justify-between"><span>7. LESS PREVIOUS CERTIFICATES</span><span>{formatCurrency(calculations.previousCertificates)}</span></div>
              <div className="flex justify-between bg-gray-100 p-2 font-bold"><span>8. CURRENT PAYMENT DUE</span><span>{formatCurrency(calculations.currentPaymentDue)}</span></div>
              <div className="flex justify-between"><span>9. BALANCE TO FINISH</span><span>{formatCurrency(calculations.balanceToFinish)}</span></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingApplicationPage;
